/**
 * Pure logic for refreshing lib/public-sound-count.json from the live catalog.
 *
 * Background (TES-1554): the snapshot is a build-time constant rendered into
 * <title>, meta descriptions and body copy on 22+ pages. Nothing refreshed it,
 * so it drifted to 1,067 while the live `is_active` catalog reached 1,392 —
 * a 23% understatement, indexed. This module turns "refresh the snapshot" into
 * a deterministic, unit-testable transform; scripts/refresh-public-sound-count.ts
 * wires it to Supabase and the weekly pipeline calls it on every ingest run.
 *
 * Two deliberate behaviours:
 *  - The public number is rounded DOWN to the nearest 10, so the "+" suffix is
 *    always a true claim and small week-to-week churn doesn't churn the repo.
 *  - A decrease larger than `maxDropPct` is REFUSED, not written. That shape is
 *    the 2026-07-08 mass-deactivation incident, and lowering the public label
 *    would quietly launder it past the tripwire in lib/sound-count-guard.ts.
 */

export interface PublicSoundCountSnapshot {
  /** Public floor, rounded down to the nearest 10. Doubles as the tripwire baseline. */
  count: number
  /** Rendered label, e.g. "1,390+". Always `count` formatted. */
  display: string
  staleTokenComment: string
  /** Retired labels the deploy gate must never find in served HTML. */
  stale: string[]
  /** ISO date of the last snapshot *change* (absent until the first refresh). */
  refreshedAt?: string
}

export interface RefreshSnapshotInput {
  current: PublicSoundCountSnapshot
  /** Live `count(*) where is_active` from the DB. */
  liveCount: number
  /** Max fractional decrease tolerated before refusing to lower the label. Default 5%. */
  maxDropPct?: number
  /** ISO date stamped onto the snapshot when it changes. */
  now: string
}

export interface RefreshSnapshotResult {
  ok: boolean
  changed: boolean
  next: PublicSoundCountSnapshot
  message: string
}

/** Round a live count down to the nearest 10 so `${n}+` is always truthful. */
export function roundDownToPublicCount(liveCount: number): number {
  if (!Number.isFinite(liveCount) || liveCount <= 0) return 0
  return Math.floor(liveCount / 10) * 10
}

export function formatPublicCountLabel(count: number): string {
  return `${count.toLocaleString('en-US')}+`
}

/** Parse a reject token ("1,390+") back to its number. Null if it isn't one. */
export function parsePublicCountLabel(token: string): number | null {
  const digits = token.replace(/[+,]/g, '')
  if (!/^\d+$/.test(digits)) return null
  return Number(digits)
}

/**
 * Could the live fetcher still render `token` for a catalog at `nextCount`?
 *
 * True for the current label itself and for anything within `maxDropPct` below
 * it — that range is ordinary curation churn, so such a label is not stale and
 * must stay OFF the reject list or the deploy gate fires on correct HTML.
 * Labels above the current count stay retired: they overstate the library.
 *
 * The "+" suffix is part of the question, not decoration. `formatPublicCountLabel`
 * always appends it, so a suffix-less token like "1,247" is something the fetcher
 * can never render at any count — the honest answer for it is always `false`,
 * independent of the band. Without this check the answer flips with the band:
 * "1,247" sat safely outside it at 1,440 ±5% and fell inside it at 1,330 ±10%
 * (the 2026-08-18 curation refresh), so the refresher would have evicted a token
 * it has no basis to call live-reachable. The deploy gate only ever compares
 * against tokens it scraped with a trailing "+", so nothing breaks today either
 * way — this keeps the eviction rule from drifting out from under a future
 * consumer that matches more loosely (TES-1767).
 */
export function isLiveReachableLabel(
  token: string,
  nextCount: number,
  maxDropPct: number,
): boolean {
  if (!token.endsWith('+')) return false
  const value = parsePublicCountLabel(token)
  if (value === null) return false
  return value <= nextCount && value >= nextCount * (1 - maxDropPct)
}

export function refreshPublicSoundCountSnapshot({
  current,
  liveCount,
  maxDropPct = 0.05,
  now,
}: RefreshSnapshotInput): RefreshSnapshotResult {
  const unchanged = { ok: true, changed: false, next: current }

  if (!Number.isFinite(liveCount) || liveCount <= 0) {
    return {
      ...unchanged,
      ok: false,
      message: `Refusing to refresh: live active count is unavailable or zero (got ${liveCount}). Snapshot left at ${current.display}.`,
    }
  }

  const nextCount = roundDownToPublicCount(liveCount)

  if (nextCount === current.count) {
    return {
      ...unchanged,
      message: `Snapshot already current: live ${liveCount} → ${current.display} (no change).`,
    }
  }

  if (nextCount < current.count) {
    const dropPct = (current.count - nextCount) / current.count
    if (dropPct > maxDropPct) {
      return {
        ...unchanged,
        ok: false,
        message:
          `Refusing to lower the public count from ${current.count} to ${nextCount}: a ` +
          `${(dropPct * 100).toFixed(1)}% drop exceeds the ${(maxDropPct * 100).toFixed(0)}% tolerance. ` +
          `This is the shape of a mass deactivation; run \`pnpm run check:sound-count\` and investigate before bumping by hand.`,
      }
    }
  }

  const nextDisplay = formatPublicCountLabel(nextCount)
  // The outgoing label becomes a reject token — but only once it is far enough
  // below the new count that the live fetcher can no longer produce it.
  //
  // Retiring the incoming label was always guarded; retiring the *outgoing* one
  // has the same defect and shipped unnoticed until the first real refresh
  // (1,390 → 1,420) put "1,390+" on the reject list while a dip to 1,395 would
  // still legitimately render it — failing the deploy gate against correct HTML.
  // `maxDropPct` already encodes "a swing this small is normal curation churn",
  // so reuse it as the band: tokens inside it are live-reachable, not stale.
  const stale = [...current.stale, current.display].filter(
    (token, index, all) =>
      all.indexOf(token) === index && !isLiveReachableLabel(token, nextCount, maxDropPct),
  )

  return {
    ok: true,
    changed: true,
    next: { ...current, count: nextCount, display: nextDisplay, stale, refreshedAt: now },
    message: `Refreshed snapshot: live ${liveCount} → ${nextDisplay} (was ${current.display}).`,
  }
}
